import { useState } from "react";
import Skeleton from "react-loading-skeleton";

import { TextTooltip, Toolbar } from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { FeedbackButton } from "@components/feedback-button";
import { ArchiveClassroomsModal } from "@components/modals";
import { useUnarchiveClassrooms } from "@components/modals/archive-classrooms/api";
import { useCurrentUser } from "@queries";
import { trackClassroomsReactivated } from "@lib/event-trackers";
import { DeleteClassroomModal } from "@views/classrooms/details/modals/delete-classroom.modal";
import { EditClassroomModal } from "@views/classrooms/details/modals/edit-classroom.modal";

export function ClassroomToolbar({
  classroom,
  onArchiveClassroomSuccess,
  onUnarchiveClassroomSuccess,
  onUnarchiveClassroomError,
  onEditClassroom,
  onDeleteClassroom,
  onPreviewClassroom,
  hasEdit,
  hasDelete,
  hasPreview,
  hasArchive,
  hasUnarchive,
  loading,
}) {
  const [openEditModal, setOpenEditModal] = useState();
  const [openDeleteModal, setOpenDeleteModal] = useState();
  const [openArchiveModal, setOpenArchiveModal] = useState();

  const { organizationCode } = useCurrentUser() || {};
  const { mutateAsync: unarchiveClassrooms, isPending: isUnarchiving } =
    useUnarchiveClassrooms({ invalidateOnSuccess: true });

  const onUnarchiveHandler = async () => {
    try {
      const result = await unarchiveClassrooms({
        classroomIds: [classroom.classroomId],
        organizationCode,
      });

      trackClassroomsReactivated([classroom], {
        source: "detail",
      });
      onUnarchiveClassroomSuccess?.(result);
    } catch (error) {
      onUnarchiveClassroomError?.(error);
    }
  };

  const onCancelEditModal = () => {
    setOpenEditModal(false);
  };

  const onEditClassroomHandler = async (updatedClassroom) => {
    return await onEditClassroom(updatedClassroom).then(setOpenEditModal);
  };

  const onDeleteClassroomHandler = () => {
    return onDeleteClassroom().then(setOpenDeleteModal);
  };

  const previewButton = hasPreview && !classroom?.isArchived && (
    <TextTooltip
      theme="preview-classroom"
      position="bottom"
      text={`
        <div class="tooltip-content-container">
          Preview learner experience
        </div class="tooltip-header">
        <div class="tooltip-subtitle">
          Access all classroom content. No data will be tracked and credentials are disabled.
        </div>
    `}
    >
      <Button
        icon={["fa-regular", "fa-eye"]}
        onClick={onPreviewClassroom}
        title="Preview"
        label="Preview"
      />
    </TextTooltip>
  );
  const editButton = hasEdit && (
    <Button
      icon={["fa-regular", "pen-to-square"]}
      onClick={() => setOpenEditModal(true)}
      title={"Edit Classroom"}
      tooltip={"Edit Classroom"}
      size="small"
    ></Button>
  );

  const deleteButton = false && (
    <Button
      icon={["fa-regular", "trash-can"]}
      onClick={() => setOpenDeleteModal(true)}
      title={"Delete Classroom"}
      tooltip={"Delete Classroom"}
    ></Button>
  );

  const archiveButton = hasArchive && (
    <Button
      icon={["fa-regular", "box-archive"]}
      onClick={() => setOpenArchiveModal(true)}
      title={"Archive Classroom"}
      tooltip={"Archive Classroom"}
    ></Button>
  );

  const unarchiveButton = hasUnarchive && (
    <Button
      icon={["fa-regular", "box-open"]}
      onClick={onUnarchiveHandler}
      title={"Unarchive Classroom"}
      tooltip={"Unarchive Classroom"}
      disabled={isUnarchiving}
    ></Button>
  );

  if (loading) {
    return (
      <div className="classroom-toolbar-skeleton">
        <Skeleton width={120} height={40} />
        <Skeleton width={42} height={40} />
      </div>
    );
  }

  return (
    <>
      <Toolbar>
        {previewButton}
        <FeedbackButton hideIcon />
        {editButton}
        {archiveButton}
        {unarchiveButton}
        {deleteButton}
      </Toolbar>
      {hasDelete && (
        <DeleteClassroomModal
          open={openDeleteModal}
          classroom={classroom}
          onCancel={setOpenDeleteModal}
          onDeleteClassroom={onDeleteClassroomHandler}
        ></DeleteClassroomModal>
      )}
      {hasEdit && (
        <EditClassroomModal
          open={openEditModal}
          classroom={classroom}
          onCancel={onCancelEditModal}
          onEditClassroom={onEditClassroomHandler}
        />
      )}
      {hasArchive && (
        <ArchiveClassroomsModal
          classrooms={[classroom]}
          xapiConfig={{ source: "detail" }}
          open={openArchiveModal}
          onCancel={() => setOpenArchiveModal()}
          onSuccess={onArchiveClassroomSuccess}
          invalidateOnSuccess
        />
      )}
    </>
  );
}
